/**
 * In-memory cache for Geoapify API results.
 * Reduces API calls for repeated routes (e.g. port → warehouse is always the same).
 */

export interface RoadDistanceCacheEntry {
  distanceKm: number;
  routeGeometry: number[][];
}

export interface GeocodeCacheEntry {
  lat: number;
  lon: number;
}

/** Round coords for cache key stability (4 decimals ≈ 11m precision) */
function coordKey(lat: number, lon: number): string {
  return `${lat.toFixed(4)},${lon.toFixed(4)}`;
}

function routingKey(origin: { lat: number; lon: number }, dest: { lat: number; lon: number }): string {
  return `routing:${coordKey(origin.lat, origin.lon)}|${coordKey(dest.lat, dest.lon)}`;
}

function geocodeKey(address: { text?: string; street?: string; housenumber?: string; postal_code?: string; city?: string; country?: string }): string {
  if (address.text) return `geocode:text:${address.text.trim().toLowerCase()}`;
  const parts = [
    address.housenumber,
    address.street,
    address.postal_code,
    address.city,
    address.country,
  ]
    .filter(Boolean)
    .map((s) => String(s).trim().toLowerCase());
  return `geocode:${parts.join(",")}`;
}

const routingCache = new Map<string, RoadDistanceCacheEntry>();
const geocodeCache = new Map<string, GeocodeCacheEntry>();

/** Max entries per cache to avoid unbounded growth */
const MAX_ROUTING_ENTRIES = 200;
const MAX_GEOCODE_ENTRIES = 500;

export function getCachedRouting(
  origin: { lat: number; lon: number },
  dest: { lat: number; lon: number },
): RoadDistanceCacheEntry | null {
  return routingCache.get(routingKey(origin, dest)) ?? null;
}

export function setCachedRouting(
  origin: { lat: number; lon: number },
  dest: { lat: number; lon: number },
  entry: RoadDistanceCacheEntry,
): void {
  if (routingCache.size >= MAX_ROUTING_ENTRIES) {
    const firstKey = routingCache.keys().next().value;
    if (firstKey) routingCache.delete(firstKey);
  }
  routingCache.set(routingKey(origin, dest), entry);
}

export function getCachedGeocode(address: {
  text?: string;
  street?: string;
  housenumber?: string;
  postal_code?: string;
  city?: string;
  country?: string;
}): GeocodeCacheEntry | null {
  return geocodeCache.get(geocodeKey(address)) ?? null;
}

export function setCachedGeocode(
  address: { text?: string; street?: string; housenumber?: string; postal_code?: string; city?: string; country?: string },
  entry: GeocodeCacheEntry,
): void {
  if (geocodeCache.size >= MAX_GEOCODE_ENTRIES) {
    const firstKey = geocodeCache.keys().next().value;
    if (firstKey) geocodeCache.delete(firstKey);
  }
  geocodeCache.set(geocodeKey(address), entry);
}
